import { MonetaryAmount } from '@components'
import type { VatReportEntry } from '@query'
import { formatDate } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { EntryPanelPagination } from './EntryPanelPagination'
import { VatEntryShimmerRows } from './EntryPanelShimmer'
import { ReportDocumentPreview } from './ReportDocumentPreview'
import {
  Caret,
  DateCell,
  EntryHeaderRow,
  EntryPanel,
  ReportRow,
  RowLabel,
  RowMeta,
  ValueCell
} from './interactive.styled'

const entryVatAmount = (entry: VatReportEntry): number => {
  if (Math.abs(entry.debet) >= Math.abs(entry.credit)) return entry.debet
  return entry.credit
}

type Props = {
  lineId: string
  entries: VatReportEntry[]
  currency?: string
  isLoading?: boolean
  entriesPage: number
  entriesPageCount: number
  entriesTotalCount: number
  entriesPageSize?: number
  onEntriesPageChange: (page: number) => void
}

export const VatLineEntries: React.FC<Props> = ({
  lineId,
  entries,
  currency,
  isLoading = false,
  entriesPage,
  entriesPageCount,
  entriesTotalCount,
  entriesPageSize,
  onEntriesPageChange
}) => {
  const [t] = useTranslation()
  const showPagination = entriesTotalCount > 0

  return (
    <EntryPanel>
      <EntryHeaderRow>
        <Caret />
        <DateCell $header>{t('reports.interactive.date')}</DateCell>
        <RowLabel $header>{t('reports.interactive.vat.voucherAccountDescription')}</RowLabel>
        <ValueCell $header>{t('reports.interactive.vat.vatAmount')}</ValueCell>
      </EntryHeaderRow>
      {isLoading ? (
        <VatEntryShimmerRows />
      ) : (
        entries.map((entry, index) => (
          <ReportRow key={`${lineId}-e-${index}`} $entryRow $bg="#f7fafd">
            <Caret />
            <DateCell>{entry.document_date ? formatDate(entry.document_date) : ''}</DateCell>
            <RowLabel $link>
              {entry.document_id ? (
                <ReportDocumentPreview documentId={entry.document_id}>
                  {entry.document_number}
                </ReportDocumentPreview>
              ) : (
                entry.document_number
              )}
              <RowMeta>
                {[entry.account, entry.document_description].filter(Boolean).join(' · ')}
              </RowMeta>
            </RowLabel>
            <ValueCell>
              <MonetaryAmount value={entryVatAmount(entry)} currency={currency} />
            </ValueCell>
          </ReportRow>
        ))
      )}
      {showPagination && (
        <EntryPanelPagination
          page={entriesPage}
          pageCount={entriesPageCount}
          totalCount={entriesTotalCount}
          pageSize={entriesPageSize}
          onPageChange={onEntriesPageChange}
        />
      )}
    </EntryPanel>
  )
}
